/**
 * Logger Utility
 *
 * Centralized logging for the application.
 * - Standard logs are only output in 'test' mode
 * - Critical errors (version/chunk failures) always log to console
 *   for production visibility and debugging
 * - Firestore telemetry is ALWAYS recorded (prod + test) for cost tracking
 */

import { useTelemetryStore } from '../store/telemetryStore';

const isTestMode = import.meta.env.MODE === 'test';

/**
 * Parse cache/server source from snapshot log string
 * Looks for patterns like "from cache" or "from server"
 */
function parseSource(pathOrDetails: string): 'cache' | 'server' | undefined {
  const lower = pathOrDetails.toLowerCase();
  if (lower.includes('from cache')) {
    return 'cache';
  }
  if (lower.includes('from server')) {
    return 'server';
  }
  return undefined;
}

/**
 * Parse document count from snapshot log string
 * Looks for pattern like "500 docs" or "1 doc"
 */
function parseDocCount(pathOrDetails: string): number {
  const match = pathOrDetails.match(/(\d+)\s+docs?/i);
  return match ? parseInt(match[1], 10) : 1;
}

/**
 * Parse changes count from snapshot log string
 * Looks for pattern like "10 changes" or "0 changes"
 * This represents documents that actually changed (billable server reads)
 */
function parseChangesCount(pathOrDetails: string): number {
  const match = pathOrDetails.match(/(\d+)\s+changes?/i);
  return match ? parseInt(match[1], 10) : 0;
}

// =============================================================================
// Logger
// =============================================================================

export const logger = {
  log: (message: string, ...args: unknown[]) => {
    if (isTestMode) console.log(message, ...args);
  },

  warn: (message: string, ...args: unknown[]) => {
    if (isTestMode) console.warn(message, ...args);
  },

  error: (message: string, ...args: unknown[]) => {
    // Only show errors in test mode
    if (isTestMode) console.error(message, ...args);
  },

  /**
   * Critical errors that ALWAYS log to console, even in production.
   * Use sparingly for issues that need visibility for debugging:
   * - Chunk load failures
   * - Version detection failures
   * - Authentication failures
   */
  critical: (message: string, ...args: unknown[]) => {
    console.error(`[CRITICAL] ${message}`, ...args);
  },

  /**
   * Specialized logger for Firestore interactions
   *
   * IMPORTANT: This function serves two purposes:
   * 1. Console logging (test mode only) - for debugging
   * 2. Telemetry recording (always) - for cost tracking
   *
   * The telemetry store accumulates stats between heartbeats (60s)
   * and sends them to the backend for analysis.
   */
  firestore: (
    action: 'Read' | 'Write' | 'Subscribe' | 'Unsubscribe' | 'Snapshot',
    pathOrDetails: string,
    data?: unknown
  ) => {
    // =======================================================
    // TELEMETRY RECORDING (always, prod + test)
    // =======================================================
    try {
      const state = useTelemetryStore.getState();

      switch (action) {
        case 'Subscribe':
          state.addListener(pathOrDetails);
          break;

        case 'Unsubscribe':
          state.removeListener(pathOrDetails);
          break;

        case 'Snapshot': {
          // Parse source and counts from log string like:
          // "rentals (523 docs, 10 changes, from server)"
          const source = parseSource(pathOrDetails);
          const docCount = parseDocCount(pathOrDetails);
          const changesCount = parseChangesCount(pathOrDetails);

          // For billing accuracy:
          // - Cache snapshots: count total docs as cache hits (served locally, free)
          // - Server snapshots: count only CHANGES as server reads (billable)
          //   (With includeMetadataChanges=true, we get 2 snapshots per load:
          //    cache first, then server. The server snapshot with 0 changes
          //    is just a metadata sync, not a billable read.)
          const countForTelemetry = source === 'server' ? changesCount : docCount;
          state.recordEvent(action, pathOrDetails, source, countForTelemetry);
          break;
        }

        case 'Read':
        case 'Write':
          state.recordEvent(action, pathOrDetails);
          break;
      }
    } catch {
      // Store not initialized yet - ignore during early bootstrap
    }

    // =======================================================
    // CONSOLE LOGGING (test mode only)
    // =======================================================
    if (isTestMode) {
      const label = `🔥 ${action}: ${pathOrDetails}`;

      // Use collapsed group for cleaner console
      console.groupCollapsed(label);
      if (data) {
        console.log('Data/Details:', data);
      }
      console.trace('Trace'); // Helpful to see WHERE the call came from
      console.groupEnd();
    }
  },

  /**
   * Specialized logger for Realtime Database interactions
   *
   * Unlike Firestore which bills per-document-read, RTDB bills per bandwidth.
   * We track:
   * - subscribe/unsubscribe for active listeners
   * - snapshot for data received (with size estimate)
   * - write for any write operations
   *
   * Note: RTDB telemetry is tracked separately from Firestore since billing models differ.
   * RTDB costs are primarily bandwidth-based, so we focus on data size rather than operation count.
   */
  rtdb: (
    action: 'subscribe' | 'unsubscribe' | 'snapshot' | 'write',
    path: string,
    details?: {
      exists?: boolean;
      size?: number;
      operation?: string;
      count?: number;
      pathCount?: number;
      /**
       * For 'snapshot' events: whether this is the FIRST snapshot of the
       * listener's lifecycle (initial sync, full-size stringify) vs a
       * subsequent delta event (no full-node stringify — see
       * rtdbSnapshotTelemetry.ts). Undefined for non-snapshot actions.
       */
      isInitialSync?: boolean;
    }
  ) => {
    // =======================================================
    // RTDB TELEMETRY RECORDING (always, prod + test)
    // Uses dedicated RTDB store methods for bandwidth-based tracking
    // =======================================================
    try {
      const state = useTelemetryStore.getState();

      switch (action) {
        case 'subscribe':
          state.addRtdbListener(path);
          break;

        case 'unsubscribe':
          state.removeRtdbListener(path);
          break;

        case 'snapshot':
          state.recordRtdbEvent('Snapshot', path, details?.size || 0, details?.isInitialSync);
          break;

        case 'write':
          state.recordRtdbEvent('Write', path, details?.size || 0);
          break;
      }
    } catch {
      // Store not initialized yet - ignore during early bootstrap
    }

    // =======================================================
    // CONSOLE LOGGING (test mode only)
    // =======================================================
    if (isTestMode) {
      const sizeInfo = details?.size ? ` (${Math.round(details.size / 1024)}KB)` : '';
      const opInfo = details?.operation ? ` [${details.operation}]` : '';
      const countInfo = details?.count ? ` (${details.count} items)` : '';
      const pathCountInfo = details?.pathCount ? ` (${details.pathCount} paths)` : '';
      const syncInfo =
        details?.isInitialSync === true ? ' [initial-sync]' : details?.isInitialSync === false ? ' [delta]' : '';
      const label = `🔄 RTDB ${action}: ${path}${sizeInfo}${opInfo}${countInfo}${pathCountInfo}${syncInfo}`;

      console.groupCollapsed(label);
      if (details) {
        console.log('Details:', details);
      }
      console.trace('Trace');
      console.groupEnd();
    }
  },
};
